import React, { useState, useEffect, useRef, useCallback } from "react";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Drawer,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { llmApi } from "@/services/rag-service";

import CrossIcon from "@/assets/icons/CrossIcon";
import CopyIcon from "@/assets/icons/CopyIcon";
import DocumentationIcon from "@/assets/icons/DocumentationIcon";
import RightArrowUpwordIcon from "@/assets/icons/RightArrowUpwordIcon";
import DotsIcon from "@/assets/icons/DotsIcon";

import ProviderIcon from "./ProviderIcon";
import Configurations from "./Configurations";
import Snippet from "./Snippet";
import { useAuth } from "../../contexts/AuthContext";
import { ModelTypeBadge, ModelVisibilityBadge } from "./components/ModelBadges";
import useCopyToClipboard from "./hooks/useCopyToClipboard";
import type { LLMModel } from "./types";

type Props = {
  selectedModelId?: string;
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  handleOpenModal: (id: string | null, isPublic: boolean) => void;
  handleOpenDeleteModal: (model: LLMModel) => void;
};

/* ================= MAIN ================= */

const AICatalogDetailComp: React.FC<Props> = ({
  selectedModelId,
  open,
  onClose,
  isAdmin = false,
  handleOpenModal,
  handleOpenDeleteModal,
}) => {
  const { cache } = useAuth();
  const { mode } = useThemeMode();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [topHeight, setTopHeight] = useState<number>(400);
  const [model, setModel] = useState<LLMModel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const copyToClipboard = useCopyToClipboard();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef<boolean>(false);
  const hasFetched = useRef<boolean>(false);
  const canManageModel = Boolean(
    model && (isAdmin || model.user_id === cache?.id),
  );

  /* ================= HANDLERS ================= */

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const containerTop = containerRef.current.getBoundingClientRect().top;
    const newHeight = e.clientY - containerTop;

    setTopHeight(Math.max(150, newHeight)); // prevent collapse
  }, []);

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  /* ================= API ================= */

  const getLLMConfig = useCallback(async () => {
    if (!selectedModelId) return;

    try {
      setLoading(true);
      setModel(null);

      const response = (await llmApi.getLLMConfig(
        selectedModelId,
      )) as unknown as LLMModel;
      setModel(response);
    } catch (error) {
      console.error(error);
      toast.error(t("aiCatalog.failedToLoadModel"));
    } finally {
      setLoading(false);
      hasFetched.current = false;
    }
  }, [selectedModelId, t]);

  /* ================= EFFECTS ================= */

  useEffect(() => {
    if (selectedModelId && !hasFetched.current) {
      hasFetched.current = true;
      getLLMConfig();
    }
  }, [selectedModelId, getLLMConfig]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  /* ================= RENDER ================= */

  return (
    <Drawer anchor={isRTL ? "left" : "right"} open={open} onClose={onClose}>
      <Box className="w-[400px] bg-[var(--card)]">
        {loading ? (
          <Box className="flex items-center justify-center h-screen">
            <CircularProgress />
          </Box>
        ) : !model ? (
          <Box className="flex items-center justify-center h-screen">
            <Typography className="text-sm font-medium text-[var(--foreground)]">
              {t("common.noDataAvailable")}
            </Typography>
          </Box>
        ) : (
          <Box ref={containerRef} className="h-screen flex flex-col">
            {/* TOP SECTION */}
            <Box className="flex-1 overflow-y-auto">
              <Box style={{ height: topHeight }} className="overflow-auto">
                {/* HEADER */}
                <Box className="px-2 py-4">
                  <Box className="flex items-center justify-between mb-2">
                    <Typography className="text-xs text-[var(--muted-foreground)]">
                      {t("deploymentDetail.modelDetails")}
                    </Typography>

                    <Box
                      onClick={onClose}
                      className="cursor-pointer"
                      data-testid="ai-catalog-detail-close-button"
                    >
                      <CrossIcon className="text-[var(--muted-foreground)]" />
                    </Box>
                  </Box>

                  {/* MODEL INFO */}
                  <Box className="flex justify-between items-center mb-2">
                    <Box className="flex items-center gap-2">
                      <Box className="relative">
                        <ProviderIcon provider={model.provider} />
                        <ModelVisibilityBadge model={model} />
                      </Box>

                      <Box>
                        <Typography className="text-sm text-[var(--foreground)]">
                          {model.name}
                        </Typography>
                        <Typography className="text-xs text-[var(--muted-foreground)]">
                          {model.provider}
                        </Typography>
                      </Box>
                    </Box>

                    {/* ACTIONS */}
                    {canManageModel && (
                      <Box className="flex gap-2">
                        <Box
                          data-testid={`ai-catalog-detail-edit-model-${model.id}`}
                          className="cursor-pointer w-8 h-8 flex items-center justify-center bg-[var(--muted)] rounded"
                          onClick={() => {
                            onClose();
                            handleOpenModal(model.id, Boolean(model.is_public));
                          }}
                        >
                          <EditIcon className="text-[var(--foreground)] text-base" />
                        </Box>

                        <Box
                          data-testid={`ai-catalog-detail-delete-model-${model.id}`}
                          className="cursor-pointer w-8 h-8 flex items-center justify-center bg-[var(--destructive)] rounded"
                          onClick={() => handleOpenDeleteModal(model)}
                        >
                          <DeleteIcon className="text-[var(--foreground)] text-base" />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                {/* ENDPOINT */}
                {model.endpoint && (
                  <Box className="px-2 mb-3">
                    <Typography className="text-xs text-[var(--muted-foreground)] mb-1">
                      {t("collections.endpointUrl")}
                    </Typography>

                    <TextField
                      data-testid="ai-catalog-detail-endpoint-field"
                      value={model.endpoint}
                      fullWidth
                      disabled
                      inputProps={{
                        "data-testid": "ai-catalog-detail-endpoint-input",
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box
                              data-testid={`ai-catalog-detail-copy-endpoint-${model.id}`}
                              className="cursor-pointer"
                              onClick={() => copyToClipboard(model.endpoint!)}
                            >
                              <CopyIcon className="" />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}

                {/* MODEL TYPE */}
                <Box className="px-2 py-2">
                  <ModelTypeBadge model={model} t={t} />
                </Box>

                {/* CONFIG */}
                <Configurations model={model} t={t} />
              </Box>

              {/* RESIZER */}
              <Box
                onMouseDown={handleMouseDown}
                className="h-1 cursor-row-resize bg-[var(--border)] flex justify-center"
              >
                <DotsIcon className="" />
              </Box>

              {/* SNIPPET */}
              <Box className="flex-1 overflow-auto">
                <Snippet
                  model={model}
                  t={t}
                  mode={mode as "light" | "dark"}
                />
              </Box>
            </Box>

            {/* FOOTER */}
            <Box className="flex justify-between p-2 cursor-pointer">
              <Box className="flex items-center gap-1">
                <DocumentationIcon className="" />
                <Typography>{t("aiCatalog.documentation")}</Typography>
              </Box>
              <RightArrowUpwordIcon className="rtl:scale-x-[-1]" />
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AICatalogDetailComp;
